// SALDP white-labelling. Added by Sullivan Digital, 2026-10-26. Licensed under the GNU AGPL v3; see LICENSE-AGPL.txt.
import type { ResolvedColorScheme } from "metabase/utils/color-scheme";
import type { ColorSettings } from "metabase-types/api";

/**
 * The colours an admin sets on the Branding page. Each is stored in the
 * `application-colors` setting under its own name for light mode, and with a
 * `dark:` prefix for dark mode. Every other token is derived from these.
 */
export const BRAND_COLOR_FIELDS = [
  "brand",
  "filter",
  "summarize",
  "link",
] as const;

export type BrandColorField = (typeof BRAND_COLOR_FIELDS)[number];

const DARK_PREFIX = "dark:";

export function getBrandColorSettingKey(
  field: BrandColorField,
  colorScheme: ResolvedColorScheme,
): string {
  return colorScheme === "dark" ? `${DARK_PREFIX}${field}` : field;
}

/**
 * Picks the palette for `colorScheme` out of the `application-colors` setting,
 * as token overrides. Chart accents apply in both modes; an unset dark colour
 * keeps the default dark palette rather than borrowing the light one.
 */
export function resolveBrandColors(
  colors: ColorSettings | null | undefined,
  colorScheme: ResolvedColorScheme,
): ColorSettings {
  const resolved: ColorSettings = Object.fromEntries(
    Object.entries(colors ?? {}).filter(([key]) => key.startsWith("accent")),
  );

  for (const field of BRAND_COLOR_FIELDS) {
    const value = colors?.[getBrandColorSettingKey(field, colorScheme)];
    if (!value) {
      continue;
    }
    if (field === "link") {
      const hoverMix = colorScheme === "dark" ? "white" : "black";
      resolved["text-brand"] = value;
      resolved["text-brand-hover"] =
        `color-mix(in srgb, ${value} 80%, ${hoverMix})`;
    } else {
      resolved[field] = value;
    }
  }

  return resolved;
}
